import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import profileImage from "../../assets/images/profile_image.png";
import {
  getStakeSettingsAsync,
  updateStakeSettingsAsync,
} from "../../store/slices/betSlice";
import { changePasswordAsync } from "../../store/slices/profileSlice";
import { BRAND_NAME_WITHOUT_SUBDOMAIN } from "../../env";

const Profile = () => {
  const location = useLocation();
  const dispatch = useDispatch();
  const { loading: changePasswordLoading } = useSelector(
    (state) => state.profile || {},
  );
  const authUser = useSelector((state) => state.auth?.user || {});
  const balance = useSelector((state) => state.bet?.balance?.balance ?? 0);
  const exposure = useSelector((state) => state.bet?.balance?.liability ?? 0);

  const isDemoUser = authUser?.is_demo;

  const brandNameWithoutSubdomain = BRAND_NAME_WITHOUT_SUBDOMAIN();
  const positive = (num) => {
    const n = Number(num);
    if (!Number.isFinite(n)) return 0;
    return Math.abs(n);
  };
  const totalChips = balance + positive(exposure);

  const { stakeSettings, stakeSettingsLoaded } = useSelector(
    (state) => state.bet || {},
  );

  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordErrors, setPasswordErrors] = useState({
    old: "",
    next: "",
    confirm: "",
  });
  const [stakeValues, setStakeValues] = useState([]);

  let activeTab = "overview";
  if (location.pathname.endsWith("/stake-settings")) {
    activeTab = "stake";
  } else if (location.pathname.endsWith("/change-password")) {
    activeTab = "password";
  }

  useEffect(() => {
    if (activeTab === "stake" && !stakeSettingsLoaded) {
      dispatch(getStakeSettingsAsync());
    }
  }, [activeTab, dispatch, stakeSettingsLoaded]);

  useEffect(() => {
    if (Array.isArray(stakeSettings) && stakeSettings.length) {
      setStakeValues(stakeSettings.map((value) => value ?? ""));
    }
  }, [stakeSettings]);

  const passwordMeetsRules = useMemo(() => {
    if (!newPassword || newPassword.length < 6) return false;
    const hasUpper = /[A-Z]/.test(newPassword);
    const hasLower = /[a-z]/.test(newPassword);
    const hasNumber = /[0-9]/.test(newPassword);
    return hasUpper && hasLower && hasNumber;
  }, [newPassword]);

  const handleStakeChange = (index, value) => {
    setStakeValues((prev) => {
      const next = Array.isArray(prev) ? [...prev] : [];
      next[index] = value;
      return next;
    });
  };

  const handleStakeSave = async () => {
    const original = Array.isArray(stakeSettings) ? stakeSettings : [];
    const current = Array.isArray(stakeValues) ? stakeValues : [];

    const unchanged =
      original.length === current.length &&
      original.every((val, idx) => Number(val) === Number(current[idx] ?? val));

    if (unchanged) {
      toast.error("Update value to save.");
      return;
    }

    const normalized = (current || []).map((val) => {
      const asNumber = Number(val);
      if (!Number.isFinite(asNumber) || asNumber <= 0) {
        return 0;
      }
      return asNumber;
    });

    const payload = {
      match_stack: normalized,
    };

    try {
      const result = await dispatch(updateStakeSettingsAsync(payload)).unwrap();

      const message =
        result?.msg || result?.message || "Stake settings updated.";
      toast.success(message);

      dispatch(getStakeSettingsAsync());
    } catch (error) {
      const message =
        typeof error === "string"
          ? error
          : error?.message || "Failed to update stake settings.";
      toast.error(message);
    }
  };

  const handleChangePasswordSubmit = async (event) => {
    event.preventDefault();

    const nextErrors = {
      old: "",
      next: "",
      confirm: "",
    };

    if (!currentPassword) {
      nextErrors.old = "The Old Password field is required";
    }

    if (!passwordMeetsRules) {
      nextErrors.next =
        "The New Password must be at least 6 characters and contain 1 uppercase, 1 lowercase letter, 1 number";
    }

    if (newPassword !== confirmPassword) {
      nextErrors.confirm = "The Confirm Password confirmation does not match";
    }

    if (nextErrors.old || nextErrors.next || nextErrors.confirm) {
      setPasswordErrors(nextErrors);
      toast.error("Please fix the errors in the form.");
      return;
    }

    setPasswordErrors({
      old: "",
      next: "",
      confirm: "",
    });

    try {
      const payload = {
        old_password: currentPassword,
        new_password: newPassword,
        confirm_password: confirmPassword,
      };

      const result = await dispatch(changePasswordAsync(payload)).unwrap();

      const message =
        result?.msg || result?.message || "Password changed successfully.";
      toast.success(message);

      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
      setPasswordErrors({
        old: "",
        next: "",
        confirm: "",
      });
    } catch (error) {
      const message =
        typeof error === "string"
          ? error
          : error?.message || "Failed to change password.";
      toast.error(message);
    }
  };

  return (
    <section className="section profile">
      <div className="row g-0">
        <div className="col-lg-12">
          <h2 className="userscreen-title">profile</h2>
        </div>
        <div className="col-xl-4 pe-xl-1 mb-3 mb-xl-0">
          <div className="card">
            <div className="card-body profile-card pt-4 d-flex flex-column align-items-center">
              <img
                src={profileImage}
                alt="Profile"
                className="rounded-circle"
              />
              <h2>{authUser?.user_name || authUser?.name || "User"}</h2>
            </div>
          </div>
        </div>
        <div className="col-xl-8 ps-xl-1">
          <div className="card">
            <div className="card-body">
              <div className="tab-container">
                <ul
                  role="tablist"
                  className="nav nav-tab nav-tabs-bordered"
                  aria-label="Tabs"
                >
                  <li
                    className={`nav-item ${
                      activeTab === "overview" ? "active" : ""
                    }`}
                  >
                    <Link
                      to="/profile"
                      role="tab"
                      className={`nav-link ${
                        activeTab === "overview" ? "active" : ""
                      }`}
                      aria-controls=""
                      aria-selected={activeTab === "overview"}
                    >
                      <span></span>
                      <button className="nav-link " tabIndex="0">
                        Overview
                      </button>
                    </Link>
                  </li>
                  <li
                    className={`nav-item ${
                      activeTab === "stake" ? "active" : ""
                    }`}
                  >
                    <Link
                      to="/profile/stake-settings"
                      role="tab"
                      className={`nav-link ${
                        activeTab === "stake" ? "active" : ""
                      }`}
                      aria-controls=""
                      aria-selected={activeTab === "stake"}
                    >
                      <span></span>
                      <button className="nav-link " tabIndex="0">
                        Stack Settings
                      </button>
                    </Link>
                  </li>
                  {!isDemoUser && (
                    <li
                      className={`nav-item ${
                        activeTab === "password" ? "active" : ""
                      }`}
                    >
                      <Link
                        to="/profile/change-password"
                        role="tab"
                        className={`nav-link ${
                          activeTab === "password" ? "active" : ""
                        }`}
                        aria-controls=""
                        aria-selected={activeTab === "password"}
                      >
                        <span></span>
                        <button className="nav-link " tabIndex="0">
                          Change Password
                        </button>
                      </Link>
                    </li>
                  )}
                </ul>
                <div className="tab-content">
                  <div
                    role="tabpanel"
                    aria-labelledby=""
                    className={`tab-pane ${
                      activeTab === "overview" ? "active" : ""
                    }`}
                    hidden={activeTab !== "overview"}
                  >
                    <div className="profile-overview ">
                      <h5 className="card-title">
                        Welcome to {brandNameWithoutSubdomain},{" "}
                        <b>
                          {isDemoUser
                            ? "Demo"
                            : authUser?.user_name || authUser?.name || "User"}
                        </b>
                      </h5>
                      <div className="row border-bottom">
                        <div className="col-lg-3 col-4 label">User Id :</div>
                        <div className="col-lg-9 col-8">
                          {authUser?.user_name || authUser?.name || "-"}
                        </div>
                      </div>
                      <div className="row border-bottom">
                        <div className="col-lg-3 col-4 label">
                          Available Chips :
                        </div>
                        <div className="col-lg-9 col-8">
                          {(balance ?? 0).toFixed(2)}/-
                        </div>
                      </div>
                      <div className="row border-bottom">
                        <div className="col-lg-3 col-4 label">exposure :</div>
                        <div className="col-lg-9 col-8">
                          {exposure.toFixed(2) ?? 0}
                        </div>
                      </div>
                      <div className="row">
                        <div className="col-lg-3 col-4 label">
                          Total Chips :
                        </div>
                        <div className="col-lg-9 col-8">
                          {(totalChips ?? 0).toFixed(2)}/-
                        </div>
                      </div>
                    </div>
                  </div>
                  <div
                    role="tabpanel"
                    aria-labelledby=""
                    className={`tab-pane ${
                      activeTab === "stake" ? "active" : ""
                    }`}
                    hidden={activeTab !== "stake"}
                  >
                    <div>
                      <form
                        noValidate
                        className="stack-setting "
                        onSubmit={(event) => {
                          event.preventDefault();
                          handleStakeSave();
                        }}
                      >
                        <div className="row my-2">
                          {(stakeValues && stakeValues.length
                            ? stakeValues
                            : stakeSettings || []
                          ).map((label, index) => (
                            <div className="col-6 " key={index}>
                              <div className="input-group">
                                <span
                                  id="basic-addon1"
                                  className="input-group-text"
                                >
                                  <b>
                                    <small>{label}</small>
                                  </b>
                                </span>
                                <input
                                  type="number"
                                  className="form-control "
                                  id={`stackVal${index}`}
                                  value={
                                    stakeValues[index] !== undefined
                                      ? stakeValues[index]
                                      : ""
                                  }
                                  onChange={(event) =>
                                    handleStakeChange(index, event.target.value)
                                  }
                                />
                              </div>
                            </div>
                          ))}
                        </div>
                        <button type="submit" className="btn btn-thmemes mb-2">
                          Save
                        </button>
                      </form>
                    </div>
                  </div>
                  <div
                    role="tabpanel"
                    aria-labelledby=""
                    className={`tab-pane ${
                      activeTab === "password" ? "active" : ""
                    }`}
                    hidden={activeTab !== "password"}
                  >
                    <form
                      noValidate
                      className="cp_form"
                      onSubmit={handleChangePasswordSubmit}
                    >
                      <div className="row mb-lg-3 mt-lg-2 align-items-center">
                        <label
                          htmlFor="currentPassword"
                          className="col-md-3 col-lg-3 col-form-label text-lg-end"
                        >
                          Current Password :
                        </label>
                        <div className="col-md-9 col-lg-9">
                          <input
                            type="password"
                            id="currentPassword"
                            className="form-control"
                            value={currentPassword}
                            onChange={(e) => {
                              setCurrentPassword(e.target.value);
                              if (passwordErrors.old) {
                                setPasswordErrors((prev) => ({
                                  ...prev,
                                  old: "",
                                }));
                              }
                            }}
                          />
                          {passwordErrors.old && (
                            <p className="text-danger small m-0">
                              {passwordErrors.old}
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="row mb-lg-3 mt-lg-2 align-items-center">
                        <label
                          htmlFor="newPassword"
                          className="col-md-3 col-lg-3 col-form-label text-lg-end"
                        >
                          New Password :
                        </label>
                        <div className="col-md-9 col-lg-9">
                          <input
                            type="password"
                            id="newPassword"
                            className="form-control"
                            value={newPassword}
                            onChange={(e) => {
                              setNewPassword(e.target.value);
                              if (passwordErrors.next) {
                                setPasswordErrors((prev) => ({
                                  ...prev,
                                  next: "",
                                }));
                              }
                            }}
                          />
                          {passwordErrors.next && (
                            <p className="text-danger small m-0">
                              {passwordErrors.next}
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="row mb-lg-3 mt-lg-2 align-items-center">
                        <label
                          htmlFor="renewPassword"
                          className="col-md-3 col-lg-3 col-form-label text-lg-end"
                        >
                          Re-enter New Password :
                        </label>
                        <div className="col-md-9 col-lg-9">
                          <input
                            type="password"
                            id="renewPassword"
                            className="form-control"
                            value={confirmPassword}
                            onChange={(e) => {
                              setConfirmPassword(e.target.value);
                              if (passwordErrors.confirm) {
                                setPasswordErrors((prev) => ({
                                  ...prev,
                                  confirm: "",
                                }));
                              }
                            }}
                          />
                          {passwordErrors.confirm && (
                            <p className="text-danger small m-0">
                              {passwordErrors.confirm}
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="feedback">
                        <p className="small m-0">
                          <i>
                            <b>Note:</b> The New Password field must be at least
                            6 characters
                          </i>
                        </p>
                        <p className="small m-0">
                          <i>
                            <b>Note:</b> The New Password must contain at least:
                            1 uppercase letter, 1 lowercase letter, 1 number
                          </i>
                        </p>
                      </div>
                      <div className="row">
                        <button type="submit" className="btn btn-thmemes">
                          {changePasswordLoading
                            ? "Changing..."
                            : "Change Password"}
                        </button>
                      </div>
                    </form>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Profile;
